import { Link, useParams } from 'react-router-dom';
import { ArrowRight, FileText, ExternalLink, FileDown } from 'lucide-react';
import Reveal from '../components/Reveal';
import { BTN_PRIMARY, BTN_SECONDARY, EYEBROW } from '../components/styles';
import { getProduct, siblingsOf, INSTALL_RESOURCES, WARRANTY } from '../content/products';
import NotFound from './NotFound';
import usePageMeta from '../hooks/usePageMeta';

function SpecTable({ product }) {
  const rows = [];
  if (product.dims) {
    rows.push(['Overall height', product.dims.height]);
    rows.push(['Top width', product.dims.topWidth]);
    rows.push(['Bottom width', product.dims.bottomWidth]);
  }
  if (product.weight) rows.push(['Weight', product.weight]);
  if (product.materials) {
    rows.push(['Body material', product.materials.body]);
    rows.push(['Bezel material', product.materials.bezel]);
    rows.push(['Push-in sides', product.materials.pushInSides]);
  }
  rows.push(['Auto-locking', 'Yes']);
  rows.push(['Stove position', product.size === 'Compact' ? 'Front, rear, top' : 'Front']);
  if (!rows.length) return null;
  return (
    <table className="w-full border-collapse text-left text-[15.7px]" data-testid="product-spec-table">
      <caption className="sr-only">Specifications for {product.name}</caption>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-b border-line2">
            <th scope="row" className="py-3 pr-6 font-mono text-[11.2px] font-medium uppercase tracking-[0.2em] text-mute">{k}</th>
            <td className="py-3 text-dim">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProduct(slug);
  usePageMeta(
    product ? `${product.name} Stove Safety Knob | RangeSafe` : 'Product | RangeSafe Commercial Solutions',
    product ? `${product.name}: ${product.tagline} Request a consultation with the RangeSafe Commercial Solutions Team.` : undefined
  );

  if (!product) return <NotFound />;

  const siblings = siblingsOf(product).slice(0, 4);

  return (
    <main className="pt-16" data-testid="product-detail">
      <div className="mx-auto max-w-shell px-5 py-12 sm:px-8 lg:py-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mute">
          <Link to="/products" className="hover:text-white" data-testid="product-breadcrumb">Products</Link>
          <span className="mx-2 text-line">/</span>
          <span className="text-dim">{product.name}</span>
        </p>

        <div className="mt-10 grid gap-14 lg:grid-cols-2">
          <Reveal>
            {product.image ? (
              <figure className="spec-frame border border-line">
                <img
                  src={product.image}
                  alt={`RangeSafe ${product.name} Stove Safety Knob, product photography`}
                  className="mx-auto h-[420px] w-full object-contain p-10 sm:h-[520px]"
                  width="768"
                  height="1024"
                />
                <figcaption className="flex flex-wrap gap-x-8 gap-y-2 border-t border-line px-6 py-4 font-mono text-[9px] uppercase tracking-[0.2em] text-mute sm:text-[10px]">
                  <span>RangeSafe Stove Safety Knobs™</span>
                  <span>{product.family} family</span>
                  <span>{product.size}</span>
                </figcaption>
              </figure>
            ) : (
              <div className="spec-frame h-[420px] border border-line sm:h-[520px]" data-testid="product-image-frame" />
            )}
          </Reveal>

          <div>
            <Reveal>
              <p className={EYEBROW}>{product.mechanism}</p>
              <h1 className="mt-4 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight text-white lg:text-6xl">
                {product.name}
              </h1>
              <p className="mt-6 text-base leading-relaxed text-dim sm:text-lg">{product.description}</p>

              <dl className="mt-8 space-y-2 border-t border-line pt-6 font-mono text-xs">
                <div className="flex justify-between gap-6">
                  <dt className="uppercase tracking-[0.2em] text-mute">Finish</dt>
                  <dd className="text-right text-dim">{product.finish}</dd>
                </div>
                <div className="flex justify-between gap-6">
                  <dt className="uppercase tracking-[0.2em] text-mute">Size</dt>
                  <dd className="text-right text-dim">{product.size}</dd>
                </div>
                <div className="flex justify-between gap-6">
                  <dt className="uppercase tracking-[0.2em] text-mute">ADA Compliant</dt>
                  <dd className="text-right text-dim">{product.family === 'Ultimate' ? 'No' : 'Yes'}</dd>
                </div>
                {product.patents.length > 0 && (
                  <div className="flex justify-between gap-6">
                    <dt className="uppercase tracking-[0.2em] text-mute">U.S. patents</dt>
                    <dd className="text-right text-dim">{product.patents.join(' · ')}</dd>
                  </div>
                )}
              </dl>

              <div className="mt-10">
                <Link to={`/quote?product=${product.slug}`} className={BTN_PRIMARY} data-testid="product-quote-cta">
                  Request a Consultation — {product.family}
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      <div className="border-t border-line bg-panel/40">
        <div className="mx-auto grid max-w-shell gap-14 px-5 py-20 sm:px-8 lg:grid-cols-3">
          <Reveal>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Specifications</h2>
            <div className="mt-6">
              <SpecTable product={product} />
              {!(product.dims || product.weight || product.materials) && (
                <p className="mt-5 text-[15.7px] leading-relaxed text-dim">
                  Published dimensions for this model are being confirmed. Ask the commercial
                  team for current documentation.
                </p>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Custom Features</h2>
            <div className="mt-6">
              <table className="w-full border-collapse text-left text-[15.7px]" data-testid="product-custom-features-table">
                <caption className="sr-only">Custom features for {product.name}</caption>
                <tbody>
                  <tr className="border-b border-line2" data-testid="product-custom-lettering">
                    <th scope="row" className="py-3 pr-6 font-mono text-[11.2px] font-medium uppercase tracking-[0.2em] text-mute">Custom lettering</th>
                    <td className="py-3 text-dim whitespace-nowrap">Available by request</td>
                  </tr>
                  <tr className="border-b border-line2" data-testid="product-custom-colors">
                    <th scope="row" className="py-3 pr-6 font-mono text-[11.2px] font-medium uppercase tracking-[0.2em] text-mute">Custom colors</th>
                    <td className="py-3 text-dim whitespace-nowrap">Available by request</td>
                  </tr>
                  <tr className="border-b border-line2" data-testid="product-custom-packaging">
                    <th scope="row" className="py-3 pr-6 font-mono text-[11.2px] font-medium uppercase tracking-[0.2em] text-mute">Custom packaging</th>
                    <td className="py-3 text-dim whitespace-nowrap">Available by request</td>
                  </tr>
                  <tr className="border-b border-line2" data-testid="product-custom-temperature-stickers">
                    <th scope="row" className="py-3 pr-6 font-mono text-[11.2px] font-medium uppercase tracking-[0.2em] text-mute">Replicate Manufacturer<br />Temperature Markings</th>
                    <td className="py-3 text-dim whitespace-nowrap">Available by request</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {product.technicalDrawing && (
              <div className="mt-10">
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Technical Information</h2>
                <div className="mt-6">
                  <a
                    href={product.technicalDrawing}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex max-w-[330px] items-center gap-4 border border-line bg-base px-5 py-8 transition-colors hover:border-accent"
                    data-testid="product-technical-drawing-link"
                  >
                    <FileDown className="h-5 w-5 shrink-0 text-accentText" aria-hidden="true" />
                    <span className="flex-1">
                      <span className="block text-[15.7px] font-medium text-white">{product.technicalDrawingLabel}</span>
                      <span className="mt-1 block font-mono text-[11.2px] uppercase tracking-[0.18em] text-mute">Dimension sheet ({product.technicalDrawing.endsWith('.pdf') ? 'PDF' : 'PNG'}) — opens in a new tab to download or print</span>
                    </span>
                    <ExternalLink className="h-4 w-4 shrink-0 text-mute" aria-hidden="true" />
                  </a>
                </div>
              </div>
            )}
          </Reveal>

          <Reveal delay={0.16}>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Installation &amp; warranty</h2>
            <p className="mt-6 text-[15.7px] leading-relaxed text-dim">
              Installation is usually simple: written instructions ship with every order and insert
              extensions are used where a particular stove requires them.
            </p>
            <a
              href={INSTALL_RESOURCES.guide.href}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 font-mono text-[12.3px] uppercase tracking-[0.2em] text-white border-b border-line pb-1 hover:border-accent"
              data-testid="product-install-guide"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden="true" /> Installation Guide (PDF)
            </a>
            <p className="mt-6 border-t border-line pt-5 text-[15.7px] leading-relaxed text-dim">{WARRANTY.term}</p>
            <Link to="/resources" className="mt-5 inline-flex items-center gap-2 font-mono text-[12.3px] uppercase tracking-[0.2em] text-white border-b border-line pb-1 hover:border-accent" data-testid="product-resources-link">
              All resources <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </div>

      <div className="border-t border-line">
        <div className="mx-auto max-w-shell px-5 py-16 sm:px-8">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute">Also in the line</h2>
          <div className="mt-8 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {siblings.map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="group bg-base p-6 transition-colors hover:bg-panel" data-testid={`sibling-${p.slug}`}>
                <img src={p.image} alt="" aria-hidden="true" className="mx-auto h-28 w-auto object-contain" width="768" height="1024" loading="lazy" />
                <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.2em] text-accentText">{p.family} · {p.size}</p>
                <p className="mt-1 font-display text-base font-bold uppercase tracking-tight text-white">{p.name}</p>
              </Link>
            ))}
          </div>
          <Link to="/products" className={`${BTN_SECONDARY} mt-10`} data-testid="back-to-products">
            <ArrowRight className="h-3.5 w-3.5 rotate-180" aria-hidden="true" /> All products
          </Link>
        </div>
      </div>
    </main>
  );
}
